import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiMapPin, FiUser } from "react-icons/fi";
import { MdOutlineComment, MdOutlineDateRange } from "react-icons/md";
import { AiOutlineFieldTime } from "react-icons/ai";
import { BsClipboardCheck } from "react-icons/bs";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { formatTime } from "../../Utils/timeUtlil";
import { Spinner } from "../../Components/Common";
import { UserContext } from "../../Context";
import { ROLES, STATUS_LABELS } from "../../Constants/Strings";
import { HOME } from "../../routes";
import { getDayById, updateDay } from "../../Services/TrackerServices";
import { MESSAGES } from "./constants";
import TaskMilestoneSection from "./Components/TaskMilestoneSection";


const ViewDayPage = () => {
  const { dayId: id } = useParams();
  const [dayData, setDayData] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState([]);
  const [editableComment, setEditableComment] = useState("");
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === ROLES.ADMIN;

  const [showStartMap, setShowStartMap] = useState(false);
  const [showEndMap, setShowEndMap] = useState(false);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getDayById(id)
        .then((res) => {
          setDayData(res.data);
          setEditableComment(res.data.comment || "");
        })
        .catch((err) => toast.error(MESSAGES.DAY_LOAD_ERROR))
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  const handleToggle = (taskId) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleCommentUpdate = async () => {
    try {
      setIsUpdatingComment(true);
      const response = await updateDay(id, { comment: editableComment });

      if (response?.status === 200 || response?.status === 201) {
        toast.success(MESSAGES.COMMENT_UPDATE_SUCCESS);
        const updated = await getDayById(id);
        setDayData(updated.data);
        setEditableComment(updated.data.comment || "");
      }
    } catch (err) {
      toast.error(MESSAGES.COMMENT_UPDATE_ERROR);
    } finally {
      setIsUpdatingComment(false);
    }
  };

  if (!dayData) return null;

  const {
    date,
    start_time,
    end_time,
    status,
    comment,
    user_name,
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    tasks = [],
  } = dayData;

  const themeColor = "text-primary";

  const commentChanged = editableComment.trim() !== (comment || "").trim();

  if (isLoading) {
    return (
      <div className="flex justify-center mt-4">
        <Spinner size={30} />
      </div>
    );
  }

  return (
    <div className="max-h-full bg-white shadow-md p-6 border border-gray-200">
      <div className="flex pb-2 mb-2 border-b">
        <button
          type="button"
          onClick={() => navigate(HOME)}
          className="text-primary hover:text-secondary text-2xl"
          title="Back"
        >
          <HiOutlineArrowLeft />
        </button>
        <h2 className="text-2xl font-bold text-primary pl-4 gap-2">
          Day Summary
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm sm:text-base pl-10">
        <p className="flex items-center gap-2">
          <MdOutlineDateRange className={themeColor} />
          {date}
        </p>

        <p className="flex items-center gap-2">
          <FiUser className={themeColor} />
          {user_name}
        </p>

        <p className="flex items-center gap-2">
          <AiOutlineFieldTime className={themeColor} />
          {formatTime(start_time)}
        </p>

        <p className="flex items-center gap-2">
          <AiOutlineFieldTime className={themeColor} />
          {formatTime(end_time)}
        </p>

        <p className="flex items-center gap-2">
          <BsClipboardCheck className={themeColor} />
          <span className="capitalize text-primary font-semibold">
            {STATUS_LABELS[status]}
          </span>
        </p>
      </div>

      <div className="mt-4 pl-10">
        <div className="flex items-center gap-2">
          <FiMapPin className={themeColor} />
          <div className="flex gap-2 flex-wrap">
            {start_latitude && start_longitude ? (
              <button
                type="button"
                onClick={() => setShowStartMap((prev) => !prev)}
                className="text-primary hover:underline"
              >
                {showStartMap ? "Hide Start Map" : "Start"}
              </button>
            ) : (
              <span className="text-gray-400">Start</span>
            )}
            {" / "}
            {end_latitude && end_longitude ? (
              <button
                type="button"
                onClick={() => setShowEndMap((prev) => !prev)}
                className="text-primary hover:underline"
              >
                {showEndMap ? "Hide End Map" : "End"}
              </button>
            ) : (
              <span className="text-gray-400">End</span>
            )}
          </div>
        </div>

        {(showStartMap || showEndMap) && (
          <div className="mt-2 flex flex-col sm:flex-row gap-4">
            {showStartMap && start_latitude && start_longitude && (
              <div
                className={`h-64 rounded overflow-hidden border shadow-sm ${
                  showStartMap && showEndMap ? "w-full sm:w-1/2" : "w-full"
                }`}
              >
                <iframe
                  src={`https://www.google.com/maps?q=${start_latitude},${start_longitude}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Start Location"
                />
              </div>
            )}

            {showEndMap && end_latitude && end_longitude && (
              <div
                className={`h-64 rounded overflow-hidden border shadow-sm ${
                  showStartMap && showEndMap ? "w-full sm:w-1/2" : "w-full"
                }`}
              >
                <iframe
                  src={`https://www.google.com/maps?q=${end_latitude},${end_longitude}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="End Location"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editable comment */}
      {isAdmin || !!comment?.trim() ? (
        <div className="my-6 flex flex-col gap-2 text-gray-700">
          <label className="flex items-center gap-2 font-medium text-primary">
            <MdOutlineComment size={20} />
            Comment:
          </label>

          {isAdmin ? (
            <>
              <textarea
                value={editableComment}
                onChange={(e) => setEditableComment(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                rows={3}
                placeholder="Add comment..."
              />
              <button
                onClick={handleCommentUpdate}
                disabled={!commentChanged || isUpdatingComment}
                className={`self-start px-4 py-1 mt-1 text-white rounded-md transition ${
                  commentChanged
                    ? "bg-primary hover:bg-hoverColor"
                    : "bg-gray-400 cursor-not-allowed"
                } ${isUpdatingComment && "opacity-60"}`}
              >
                {isUpdatingComment ? "Updating..." : "Update Comment"}
              </button>
            </>
          ) : (
            <div className="px-3 py-2 text-sm rounded-md text-primary whitespace-pre-wrap">
              {comment}
            </div>
          )}
        </div>
      ) : null}

      {/* Task Section */}
      <div>
        <TaskMilestoneSection
          tasks={tasks}
          showIcons={true}
          expandedTasks={expandedTasks}
          onToggle={handleToggle}
        />
      </div>
    </div>
  );
};

export default ViewDayPage;
